
import { Component, Vue, Watch } from 'vue-property-decorator'
import Empty from '@/components/Empty/index.vue'
import { UserModule } from '@/store/modules/user'
import ImageUpload from '@/components/ImgUpload/index.vue'
import { listMyStores } from '@/api/store'
import {
  getNoticeList,
  createNotice,
  updateNotice,
  deleteNotice,
  noticeSetSort,
  AppNotice
} from '@/api/notice'
import SortInlineEdit from '@/components/SortInlineEdit/index.vue'

@Component({
  name: 'Notice',
  components: {
    Empty,
    ImageUpload,
    SortInlineEdit
  }
})
export default class extends Vue {
  private tableData: AppNotice[] = []
  private searchTitle: string = ''
  private searchStatus: number | null = null
  private isSearch: boolean = false
  private storeOptions: Array<{ id: number; name: string }> = []

  private dialogVisible: boolean = false
  private form: AppNotice = {
    title: '',
    content: '',
    imageUrl: '',
    linkUrl: '',
    status: 1,
    sort: 0,
    storeId: null
  }

  // 参照商品页：图片回显用单独变量，避免重置 form 对象导致子组件 prop 监听不到
  private imageUrl: string = ''

  private rules: any = {
    storeId: [{ required: true, message: '请先在顶栏选择商户', trigger: 'change' }],
    title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
    status: [{ required: true, message: '请选择状态', trigger: 'change' }]
  }

  private imageChange(url: string) {
    this.form.imageUrl = url
    this.imageUrl = url
  }

  get currentStoreId() {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  get currentStoreLabel(): string {
    const sid = this.currentStoreId
    if (sid == null) return '未选择商户'
    return this.getStoreName(sid)
  }

  @Watch('currentStoreId')
  private onStoreChange() {
    this.loadList()
  }

  created() {
    this.loadStores()
    this.loadList()
  }

  private async loadStores() {
    try {
      const res: any = await listMyStores()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.storeOptions = res.data.data.map((it: any) => ({
          id: it.id,
          name: it.storeName || it.name || `商户${it.id}`
        }))
      } else {
        this.storeOptions = []
      }
    } catch (e) {
      this.storeOptions = []
    }
  }

  private getStoreName(id?: number | null) {
    if (id == null) return '未选择'
    const found = this.storeOptions.find((s) => Number(s.id) === Number(id))
    return found ? found.name : `商户${id}`
  }

  private async loadList() {
    const storeId = this.currentStoreId
    if (!storeId) {
      this.tableData = []
      return
    }
    const params: any = {
      storeId,
      status: this.searchStatus !== null ? this.searchStatus : undefined
    }
    this.isSearch = !!this.searchTitle || this.searchStatus !== null

    try {
      const res: any = await getNoticeList(params)
      if (res.data.code === 1) {
        const list: AppNotice[] = res.data.data || []
        this.tableData = list.filter(
          (row) => row && row.storeId != null && Number(row.storeId) === storeId
        )
      }
    } catch (err) {
      this.$message.error('请求出错了：' + err.message)
    }
  }

  private setSortHandle(row: any, sort: number) {
    if (!row || !row.id) return
    noticeSetSort({ id: row.id, sort })
      .then((res: any) => {
        if (res && res.data && String(res.data.code) === '1') {
          this.$message.success('排序已更新')
          this.loadList()
        } else {
          this.$message.error((res.data && (res.data.msg || res.data.desc)) || '排序失败')
        }
      })
      .catch((err: any) => {
        this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
      })
  }

  private openDialog(row?: AppNotice) {
    const storeId = this.currentStoreId
    if (!storeId) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    if (row) {
      this.form = { ...row, storeId }
      this.imageUrl = row.imageUrl || ''
    } else {
      this.form = {
        title: '',
        content: '',
        imageUrl: '',
        linkUrl: '',
        status: 1,
        sort: null as any,
        storeId
      }
      this.imageUrl = ''
    }
    this.dialogVisible = true
    this.$nextTick(() => {
      const form: any = this.$refs.formRef
      if (form && typeof form.clearValidate === 'function') {
        form.clearValidate()
      }
    })
  }

  private handleSubmit() {
    (this.$refs.formRef as any).validate(async (valid: boolean) => {
      if (!valid) return
      const storeId = this.currentStoreId
      if (!storeId) {
        this.$message.warning('请先在顶栏选择商户')
        return
      }
      try {
        // 确保提交时 imageUrl 与上传组件同步
        this.form.imageUrl = this.imageUrl
        const payload: AppNotice = { ...this.form, storeId }
        if (!payload.id) {
          // 新增不传排序，后端按店铺现有 max+1
          delete (payload as any).sort
        }
        if (payload.id) {
          await updateNotice(payload)
        } else {
          await createNotice(payload)
        }
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.loadList()
      } catch (err) {
        this.$message.error('保存失败：' + err.message)
      }
    })
  }

  private handleDelete(row: AppNotice) {
    this.$confirm('确认删除该通知吗？', '提示', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      try {
        if (row.id) {
          await deleteNotice(row.id)
          this.$message.success('删除成功')
          this.loadList()
        }
      } catch (err) {
        this.$message.error('删除失败：' + err.message)
      }
    })
  }
}
